/**
 * InquiriesListPage - Admin queue view of all inquiries with SLA tracking
 */

import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  getInquiries,
  getShows,
  type Inquiry,
  type Show,
} from '../../../api/admin';
import { useDebounce } from '../../../hooks/useDebounce';

type InquiryStatus = 'all' | 'pending' | 'in_review' | 'responded' | 'converted' | 'closed';
type InquirySource = 'all' | 'dual_option_hosted' | 'contact_form' | 'booking_question';
type SLAStatus = 'all' | 'on_track' | 'warning' | 'breached';

export default function InquiriesListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [shows, setShows] = useState<Show[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  // Full-page skeleton only before the first load; later fetches keep the
  // page mounted and show a lightweight overlay on the list instead.
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<InquiryStatus>(
    (searchParams.get('status') as InquiryStatus) || 'all'
  );
  const [sourceFilter, setSourceFilter] = useState<InquirySource>(
    (searchParams.get('source') as InquirySource) || 'all'
  );
  const [showFilter, setShowFilter] = useState(searchParams.get('show') || '');
  const [slaFilter, setSlaFilter] = useState<SLAStatus>(
    (searchParams.get('sla') as SLAStatus) || 'all'
  );
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [page, setPage] = useState(1);

  // Sort
  type SortColumn = 'sla' | 'studio' | 'show' | 'source' | 'status' | 'submitted';
  type SortDirection = 'asc' | 'desc';
  const [sortColumn, setSortColumn] = useState<SortColumn>('submitted');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Stats
  const [stats, setStats] = useState<{
    total: number;
    pending: number;
    overdue: number;
    avgResponseTime: number | null;
  }>({
    total: 0,
    pending: 0,
    overdue: 0,
    avgResponseTime: null,
  });

  useEffect(() => {
    loadShows();
  }, []);

  useEffect(() => {
    loadInquiries();
    // Update URL params
    const params = new URLSearchParams();
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (sourceFilter !== 'all') params.set('source', sourceFilter);
    if (showFilter) params.set('show', showFilter);
    if (slaFilter !== 'all') params.set('sla', slaFilter);
    if (debouncedSearch) params.set('q', debouncedSearch);
    setSearchParams(params);
  }, [statusFilter, sourceFilter, showFilter, slaFilter, debouncedSearch, page]);

  const loadShows = async () => {
    try {
      const showsData = await getShows();
      setShows(showsData);
    } catch (err) {
      console.error('Failed to load shows:', err);
    }
  };

  const loadInquiries = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const { inquiries: inquiriesData, total: totalCount, stats: statsData } = await getInquiries({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        source: sourceFilter !== 'all' ? sourceFilter : undefined,
        show_id: showFilter || undefined,
        sla_status: slaFilter !== 'all' ? slaFilter : undefined,
        search: debouncedSearch || undefined,
        page,
        limit: 20,
      });
      setInquiries(inquiriesData);
      setTotal(totalCount);
      if (statsData) {
        setStats({
          total: statsData.total,
          pending: statsData.pending,
          overdue: statsData.overdue,
          avgResponseTime: statsData.avgResponseTime ?? null,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load inquiries');
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  };

  const getSLAStatus = (inquiry: Inquiry) => {
    if (inquiry.status === 'responded' || inquiry.status === 'converted' || inquiry.status === 'closed') {
      return null; // No SLA for closed inquiries
    }

    // Backend computes this per row (pending: >=24h breached, >=12h warning)
    if (inquiry.sla_status) {
      return inquiry.sla_status;
    }

    // Defensive fallback mirroring the backend thresholds
    if (inquiry.status === 'pending') {
      const hoursOld = (Date.now() - new Date(inquiry.created_at).getTime()) / (1000 * 60 * 60);
      if (hoursOld >= 24) return 'breached';
      if (hoursOld >= 12) return 'warning';
    }
    return 'on_track';
  };

  const getSLABadge = (inquiry: Inquiry) => {
    const slaStatus = getSLAStatus(inquiry);
    if (!slaStatus) return null;

    const styles: Record<string, string> = {
      on_track: 'bg-success-100 text-success-700',
      warning: 'bg-warning-100 text-warning-700',
      breached: 'bg-danger-100 text-danger-700',
    };
    const labels: Record<string, string> = {
      on_track: 'On Track',
      warning: 'Warning',
      breached: 'Overdue',
    };
    const icons: Record<string, string> = {
      on_track: '●',
      warning: '◐',
      breached: '○',
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[slaStatus]}`}>
        <span className="text-[10px]">{icons[slaStatus]}</span>
        {labels[slaStatus]}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-warning-100 text-warning-700',
      in_review: 'bg-primary-100 text-primary-700',
      responded: 'bg-success-100 text-success-700',
      converted: 'bg-green-100 text-green-700',
      closed: 'bg-warm-100 text-warm-600',
    };
    const labels: Record<string, string> = {
      pending: 'Pending',
      in_review: 'In Review',
      responded: 'Responded',
      converted: 'Converted',
      closed: 'Closed',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-warm-100 text-warm-600'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getSourceBadge = (source: string) => {
    const styles: Record<string, string> = {
      dual_option_hosted: 'bg-purple-100 text-purple-700',
      remote_request: 'bg-blue-100 text-blue-700',
      general: 'bg-warm-100 text-warm-600',
      contact_form: 'bg-orange-100 text-orange-700',
      booking_question: 'bg-teal-100 text-teal-700',
    };
    const labels: Record<string, string> = {
      dual_option_hosted: 'Hosted Class',
      remote_request: 'Remote Request',
      general: 'General',
      contact_form: 'Contact Form',
      booking_question: 'Booking Question',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[source] || 'bg-warm-100 text-warm-600'}`}>
        {labels[source] || source}
      </span>
    );
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTimeAgo = (date: string) => {
    const now = new Date();
    const then = new Date(date);
    const hours = Math.floor((now.getTime() - then.getTime()) / (1000 * 60 * 60));

    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  const formatResponseTime = (hours: number | null) => {
    if (hours === null || hours <= 0) return '--';
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    if (hours < 24) return `${hours.toFixed(1)}h`;
    return `${(hours / 24).toFixed(1)}d`;
  };

  const totalPages = Math.ceil(total / 20);

  const toggleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortValue = (inquiry: Inquiry, column: SortColumn): string | number => {
    switch (column) {
      case 'sla': {
        const sla = getSLAStatus(inquiry);
        const priority: Record<string, number> = { breached: 0, warning: 1, on_track: 2 };
        return sla ? (priority[sla] ?? 3) : 3;
      }
      case 'studio':
        return (inquiry.studio?.name || inquiry.contact_name || '').toLowerCase();
      case 'show':
        return (inquiry.show?.name || inquiry.show?.name || '').toLowerCase();
      case 'source':
        return inquiry.inquiry_source || '';
      case 'status':
        return inquiry.status;
      case 'submitted':
        return new Date(inquiry.created_at).getTime();
      default:
        return 0;
    }
  };

  const sortedInquiries = [...inquiries].sort((a, b) => {
    const aVal = getSortValue(a, sortColumn);
    const bVal = getSortValue(b, sortColumn);

    let cmp: number;
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      cmp = aVal - bVal;
    } else {
      cmp = String(aVal).localeCompare(String(bVal));
    }

    // Secondary sort by created_at (oldest first) when primary values are equal
    if (cmp === 0) {
      cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    }

    return sortDirection === 'asc' ? cmp : -cmp;
  });

  if (isLoading && !hasLoaded) {
    return (
      <div>
        <div className="flex items-center justify-between mb-8">
          <div className="h-10 bg-warm-200 rounded w-1/4 animate-pulse" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl p-3 sm:p-4 animate-pulse">
              <div className="h-8 bg-warm-100 rounded w-1/2 mb-2" />
              <div className="h-4 bg-warm-100 rounded w-3/4" />
            </div>
          ))}
        </div>
        <div className="bg-white rounded-xl shadow-sm">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="p-6 border-b border-warm-100 animate-pulse">
              <div className="h-6 bg-warm-100 rounded w-1/3 mb-2" />
              <div className="h-4 bg-warm-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900">Inquiries</h1>
          <p className="text-warm-600 mt-1 hidden sm:block">
            Manage studio inquiries with 24-hour response tracking.
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xl sm:text-2xl font-bold text-warm-900">{stats.total}</p>
              <p className="text-xs sm:text-sm text-warm-600">Total Inquiries</p>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary-100 flex items-center justify-center">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xl sm:text-2xl font-bold text-warning-600">{stats.pending}</p>
              <p className="text-xs sm:text-sm text-warm-600">Pending Response</p>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-warning-100 flex items-center justify-center">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-warning-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xl sm:text-2xl font-bold text-danger-600">{stats.overdue}</p>
              <p className="text-xs sm:text-sm text-warm-600">Overdue (24h+)</p>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-danger-100 flex items-center justify-center">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-danger-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xl sm:text-2xl font-bold text-success-600">
                {formatResponseTime(stats.avgResponseTime)}
              </p>
              <p className="text-xs sm:text-sm text-warm-600">Avg Response Time</p>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-success-100 flex items-center justify-center">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex flex-col gap-3">
          {/* Search - full width on mobile */}
          <div className="w-full">
            <label className="block text-sm font-medium text-warm-700 mb-1">
              Search
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                placeholder="Studio name, email..."
                className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {isLoading && hasLoaded && (
                <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-500 animate-spin" viewBox="0 0 24 24" fill="none" aria-label="Loading">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:flex gap-3">
            {/* Status Filter */}
            <div className="w-full sm:min-w-[140px] sm:w-auto">
              <label className="block text-sm font-medium text-warm-700 mb-1">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as InquiryStatus);
                  setPage(1);
                }}
                className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="in_review">In Review</option>
                <option value="responded">Responded</option>
                <option value="converted">Converted</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            {/* Source Filter */}
            <div className="w-full sm:min-w-[140px] sm:w-auto">
              <label className="block text-sm font-medium text-warm-700 mb-1">
                Source
              </label>
              <select
                value={sourceFilter}
                onChange={(e) => {
                  setSourceFilter(e.target.value as InquirySource);
                  setPage(1);
                }}
                className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Sources</option>
                <option value="dual_option_hosted">Hosted Class</option>
                <option value="contact_form">Contact Form</option>
                <option value="booking_question">Booking Question</option>
              </select>
            </div>

            {/* SLA Filter */}
            <div className="w-full sm:min-w-[140px] sm:w-auto">
              <label className="block text-sm font-medium text-warm-700 mb-1">
                Response Status
              </label>
              <select
                value={slaFilter}
                onChange={(e) => {
                  setSlaFilter(e.target.value as SLAStatus);
                  setPage(1);
                }}
                className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Statuses</option>
                <option value="on_track">On Track</option>
                <option value="warning">Warning (12-24h)</option>
                <option value="breached">Overdue (&gt;24h)</option>
              </select>
            </div>

            {/* Show Filter */}
            <div className="w-full sm:min-w-[160px] sm:w-auto">
              <label className="block text-sm font-medium text-warm-700 mb-1">
                Show
              </label>
              <select
                value={showFilter}
                onChange={(e) => {
                  setShowFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">All Shows</option>
                {shows.map((show) => (
                  <option key={show.id} value={show.id}>
                    {show.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Clear Filters */}
            {(statusFilter !== 'all' || sourceFilter !== 'all' || showFilter || slaFilter !== 'all' || searchQuery) && (
              <div className="flex items-end col-span-2 sm:col-span-1">
                <button
                  onClick={() => {
                    setStatusFilter('all');
                    setSourceFilter('all');
                    setShowFilter('');
                    setSlaFilter('all');
                    setSearchQuery('');
                    setPage(1);
                  }}
                  className="px-4 py-2.5 min-h-[44px] text-sm text-warm-600 hover:text-warm-900 hover:bg-warm-100 rounded-lg transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      )}

      {/* Inquiries List — during refetch the previous content stays mounted,
          dimmed under a spinner overlay, so filter changes never blank the page */}
      <div className="relative">
        {isLoading && hasLoaded && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 rounded-xl">
            <svg className="w-8 h-8 text-primary-500 animate-spin" viewBox="0 0 24 24" fill="none" aria-label="Loading inquiries">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          </div>
        )}
      {sortedInquiries.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-warm-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-warm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-warm-900 mb-2">No inquiries found</h3>
          <p className="text-warm-600">
            {searchQuery || statusFilter !== 'all' || sourceFilter !== 'all' || showFilter || slaFilter !== 'all'
              ? 'Try adjusting your filters to see more results.'
              : 'Inquiries will appear here when studios submit them.'}
          </p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-warm-50 border-b border-warm-100">
                <tr>
                  {([
                    { key: 'sla' as SortColumn, label: 'Status' },
                    { key: 'studio' as SortColumn, label: 'Studio' },
                    { key: 'show' as SortColumn, label: 'Show Interest' },
                    { key: 'source' as SortColumn, label: 'Source' },
                    { key: 'status' as SortColumn, label: 'Status' },
                    { key: 'submitted' as SortColumn, label: 'Submitted' },
                  ]).map((col) => (
                    <th
                      key={col.key}
                      className="text-left px-6 py-4 text-sm font-semibold text-warm-700 cursor-pointer select-none hover:text-warm-900 group"
                      onClick={() => toggleSort(col.key)}
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        <svg
                          className={`w-3.5 h-3.5 transition-colors ${
                            sortColumn === col.key ? 'text-primary-600' : 'text-warm-300 group-hover:text-warm-400'
                          }`}
                          viewBox="0 0 16 16"
                          fill="currentColor"
                        >
                          {sortColumn === col.key && sortDirection === 'asc' ? (
                            <path d="M8 4l4 5H4l4-5z" />
                          ) : sortColumn === col.key && sortDirection === 'desc' ? (
                            <path d="M8 12l4-5H4l4 5z" />
                          ) : (
                            <>
                              <path d="M8 4l3 3.5H5L8 4z" opacity={0.4} />
                              <path d="M8 12l3-3.5H5L8 12z" opacity={0.4} />
                            </>
                          )}
                        </svg>
                      </span>
                    </th>
                  ))}
                  <th className="text-right px-6 py-4 text-sm font-semibold text-warm-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-warm-100">
                {sortedInquiries.map((inquiry) => {
                  const slaStatus = getSLAStatus(inquiry);
                  const isOverdue = slaStatus === 'breached' || slaStatus === 'warning';

                  return (
                    <tr
                      key={inquiry.id}
                      className={`hover:bg-warm-50 transition-colors ${
                        isOverdue ? 'bg-danger-50/30' : ''
                      }`}
                    >
                      <td className="px-6 py-4">
                        {getSLABadge(inquiry)}
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-warm-900">
                            {inquiry.studio?.name || inquiry.contact_name || 'Unknown'}
                          </p>
                          <p className="text-sm text-warm-500">
                            {inquiry.studio?.primary_contact?.email || inquiry.contact_email}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-warm-900">
                          {inquiry.show?.name || inquiry.show?.name || 'General Inquiry'}
                        </p>
                        {inquiry.city && (
                          <p className="text-sm text-warm-500">
                            {inquiry.city.name}, {inquiry.city.state}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {getSourceBadge(inquiry.inquiry_source || 'general')}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(inquiry.status)}
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-warm-900 text-sm">
                            {formatTimeAgo(inquiry.created_at)}
                          </p>
                          <p className="text-xs text-warm-500">
                            {formatDate(inquiry.created_at)}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Link
                          to={`/admin/inquiries/${inquiry.id}`}
                          className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                        >
                          View & Respond
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {sortedInquiries.map((inquiry) => (
              <Link
                to={`/admin/inquiries/${inquiry.id}`}
                key={inquiry.id}
                className="block bg-white rounded-xl border border-warm-200 p-4 active:scale-[0.98] transition-transform hover:border-warm-300"
              >
                <div className="flex items-center gap-2 mb-2">
                  {getSLABadge(inquiry)}
                  {getSourceBadge(inquiry.inquiry_source || 'general')}
                </div>
                <p className="font-medium text-warm-900">
                  {inquiry.studio?.name || inquiry.contact_name || 'Unknown'}
                </p>
                <p className="text-sm text-warm-600">
                  {inquiry.show?.name || inquiry.show?.name || 'General Inquiry'}
                  {inquiry.city && ` \u00b7 ${inquiry.city.name}`}
                </p>
                <div className="flex items-center justify-between mt-2">
                  {getStatusBadge(inquiry.status)}
                  <span className="text-xs text-warm-500">
                    {formatTimeAgo(inquiry.created_at)}
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6">
              <p className="text-sm text-warm-600">
                Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, total)} of {total} inquiries
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2.5 min-h-[44px] border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="text-sm text-warm-600">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2.5 min-h-[44px] border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
}
